import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import DriveDetector from '../plugins/DriveDetector';

const functions = getFunctions();
const createCheckout = httpsCallable(functions, 'createCheckout');

const WORK_TYPES = [
  { id: 'self-employed', label: 'Self-Employed', desc: 'Delivery, rideshare, real estate, photography, creator, entrepreneur, etc.' },
  { id: 'employee', label: 'Employee', desc: 'Taxes are taken out of your paycheck. Examples: Healthcare, distribution, sales.' },
];

const WORK_CATEGORIES = [
  'Rideshare', 'Delivery', 'Real Estate', 'Photography', 'Consulting',
  'Home Services', 'Food Services', 'Trucking', 'Sales', 'Healthcare',
  'Insurance', 'Legal', 'Coaching', 'Creative', 'Other',
];

export default function OnboardingWizard({ onComplete }) {
  const { currentUser } = useAuth();
  const [step, setStep] = useState(0);
  const [workType, setWorkType] = useState('');
  const [workCategory, setWorkCategory] = useState('');
  const [vehicle, setVehicle] = useState({ make: '', model: '', year: '', startingMileage: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingProfile, setCheckingProfile] = useState(true);

  const currentYear = new Date().getFullYear();
  const isNative = Capacitor.isNativePlatform();
  const totalSteps = isNative ? 8 : 5;
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // On native, check if web onboarding was already completed.
  // If so, skip directly to native-only steps (location/motion/notifications).
  useEffect(() => {
    async function checkExistingProfile() {
      if (!isNative) { setCheckingProfile(false); return; }
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          const hasVehicles = data.vehicles && Object.keys(data.vehicles).length > 0;
          const webOnboardingDone = data.profile?.onboardingComplete;
          if (webOnboardingDone && hasVehicles) {
            // Web onboarding already done — skip to native permission steps
            setStep(4);
          }
        }
      } catch (e) {
        console.error('Error checking profile:', e);
      }
      setCheckingProfile(false);
    }
    checkExistingProfile();
  }, [currentUser.uid, isNative]);

  async function saveProfile() {
    setLoading(true);
    setError('');
    try {
      const vehicleId = `vehicle_${Date.now()}`;
      const updates = {
        'profile.workType': workType,
        'profile.workCategory': workCategory,
        'profile.onboardingComplete': true,
        vehicles: {
          [vehicleId]: {
            make: vehicle.make,
            model: vehicle.model,
            year: parseInt(vehicle.year, 10),
            startingMileage: vehicle.startingMileage ? parseInt(vehicle.startingMileage, 10) : 0,
            nickname: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          },
        },
      };
      if (isNative) {
        updates['profile.nativeOnboardingComplete'] = true;
      }
      await updateDoc(doc(db, 'users', currentUser.uid), updates);
      setStep(8);
    } catch (err) {
      setError('Failed to save. Please try again.');
    }
    setLoading(false);
  }

  // Save only native onboarding flag (when web profile already exists)
  async function saveNativeOnboarding() {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'profile.nativeOnboardingComplete': true,
      });
      setStep(8);
    } catch (err) {
      setError('Failed to save. Please try again.');
    }
    setLoading(false);
  }

  async function requestLocation() {
    if (!isNative) { setStep(5); return; }
    try {
      if (Capacitor.getPlatform() === 'ios') {
        // iOS: Let DriveDetector handle the full permission flow
        // It requests WhenInUse first, then upgrades to Always
        await DriveDetector.requestPermissions();
      } else {
        // Android: Must use Capacitor Geolocation first for foreground,
        // then DriveDetector for background + activity recognition
        const status = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (status.location !== 'denied') {
          try { await Geolocation.getCurrentPosition({ enableHighAccuracy: true }); } catch (e) {}
          try { await DriveDetector.requestPermissions(); } catch (e) {}
        }
      }
    } catch (e) {
      console.error('Permission request error:', e);
    }
    setStep(5);
  }

  async function requestMotion() {
    if (!isNative) { setStep(6); return; }
    try {
      // Motion permission is requested by DriveDetector via CoreMotion
      // Also request web DeviceMotionEvent permission for completeness
      if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
        await DeviceMotionEvent.requestPermission();
      }
    } catch (e) {
      console.error('Motion permission error:', e);
    }
    setStep(6);
  }

  async function requestNotifications() {
    if (!isNative) { setStep(4); return; } // web: go to subscription step
    try {
      const DriveDetector = (await import('../plugins/DriveDetector')).default;
      await DriveDetector.requestNotificationPermission();
    } catch (e) {}
    setStep(7); // native: go to subscription step
  }

  async function waitForSubscription(maxAttempts = 10) {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      if (snap.exists()) {
        const status = snap.data().subscription?.status;
        if (status === 'active' || status === 'trialing') return true;
      }
    }
    return false;
  }

  async function handleSubscribe() {
    setCheckoutLoading(true);
    try {
      const result = await createCheckout({ plan: 'standard' });
      if (result.data.url) {
        if (isNative) {
          const { Browser } = await import('@capacitor/browser');
          const listener = await Browser.addListener('browserFinished', async () => {
            listener.remove();
            // Wait for Stripe webhook to update subscription status
            await waitForSubscription();
            if (workType === '' && workCategory === '') {
              await saveNativeOnboarding();
            } else {
              await saveProfile();
            }
          });
          await Browser.open({ url: result.data.url });
        } else {
          window.location.href = result.data.url;
        }
      }
    } catch (err) {
      console.error('Checkout error:', err);
    }
    setCheckoutLoading(false);
  }

  function skipSubscription() {
    // Save profile and proceed without subscribing
    if (isNative && workType === '' && workCategory === '') {
      saveNativeOnboarding();
    } else {
      saveProfile();
    }
  }

  const progressPercent = Math.round((step / totalSteps) * 100);

  if (checkingProfile) return null;

  // Step 0: Welcome
  if (step === 0) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card onboarding-welcome">
          <div className="onboarding-illustration">
            <svg viewBox="0 0 200 160" width="200" height="160">
              <defs>
                <linearGradient id="road" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#0D9488' }} />
                  <stop offset="100%" style={{ stopColor: '#34D399' }} />
                </linearGradient>
              </defs>
              <path d="M 10 120 Q 60 40 100 80 Q 140 120 190 60" fill="none" stroke="url(#road)" strokeWidth="3" strokeLinecap="round" />
              <rect x="80" y="65" width="40" height="24" rx="6" fill="#1E293B" />
              <circle cx="86" cy="92" r="4" fill="#64748B" />
              <circle cx="114" cy="92" r="4" fill="#64748B" />
              <rect x="88" y="70" width="10" height="6" rx="2" fill="#06B6D4" opacity="0.6" />
              <rect x="102" y="70" width="10" height="6" rx="2" fill="#06B6D4" opacity="0.6" />
              <text x="100" y="40" textAnchor="middle" fontSize="16" fontWeight="800" fill="#34D399">$$$</text>
              <rect x="72" y="30" width="56" height="22" rx="6" fill="none" stroke="#334155" strokeWidth="1.5" />
            </svg>
          </div>
          <h2>Welcome to MileDeduct!</h2>
          <p className="onboarding-desc">
            Helping you track every mile and maximize your tax deductions. Let's get you set up.
          </p>
          <div className="onboarding-dots">
            {[...Array(totalSteps)].map((_, i) => (
              <span key={i} className={`dot ${i === 0 ? 'active' : ''}`} />
            ))}
          </div>
          <button className="btn btn-primary" onClick={() => setStep(1)}>
            Let's Go
          </button>
          <button className="btn-skip" onClick={() => setStep(1)}>
            I know what I'm doing
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Work Type
  if (step === 1) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card">
          <div className="onboarding-progress">
            <div className="onboarding-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>
          <h2>Choose a mode to get started</h2>
          <p className="onboarding-desc">
            We'll optimize the app based on what kind of work you do. You can always edit later.
          </p>

          <div className="onboarding-options">
            {WORK_TYPES.map((wt) => (
              <button
                key={wt.id}
                className={`onboarding-option ${workType === wt.id ? 'selected' : ''}`}
                onClick={() => setWorkType(wt.id)}
              >
                <div className="option-content">
                  <span className="option-label">{wt.label}</span>
                  <span className="option-desc">{wt.desc}</span>
                </div>
                <span className={`option-radio ${workType === wt.id ? 'checked' : ''}`} />
              </button>
            ))}
          </div>

          <button
            className="btn btn-primary"
            disabled={!workType}
            onClick={() => setStep(2)}
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Work Category
  if (step === 2) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card">
          <div className="onboarding-progress">
            <div className="onboarding-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>
          <h2>What do you do?</h2>
          <p className="onboarding-desc">Select your primary work category.</p>

          <div className="onboarding-list">
            {WORK_CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`onboarding-list-item ${workCategory === cat ? 'selected' : ''}`}
                onClick={() => setWorkCategory(cat)}
              >
                <span>{cat}</span>
                <span className={`option-radio ${workCategory === cat ? 'checked' : ''}`} />
              </button>
            ))}
          </div>

          <div className="onboarding-nav-buttons">
            <button className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
            <button
              className="btn btn-primary"
              disabled={!workCategory}
              onClick={() => setStep(3)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Vehicle
  if (step === 3) {
    const canContinue = vehicle.make && vehicle.model && vehicle.year;
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card">
          <div className="onboarding-progress">
            <div className="onboarding-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>
          <h2>Add your vehicle</h2>
          <p className="onboarding-desc" style={{ marginBottom: '12px' }}>We'll track miles for this vehicle.</p>

          {error && <div className="error-message">{error}</div>}

          <div className="form-row">
            <div className="form-group" style={{ flex: '0 0 80px', marginBottom: '8px' }}>
              <label>Year</label>
              <input
                type="number"
                value={vehicle.year}
                onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })}
                placeholder={currentYear.toString()}
                min="1900"
                max={currentYear + 1}
              />
            </div>
            <div className="form-group" style={{ marginBottom: '8px' }}>
              <label>Make</label>
              <input
                type="text"
                value={vehicle.make}
                onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })}
                placeholder="Toyota"
              />
            </div>
            <div className="form-group" style={{ marginBottom: '8px' }}>
              <label>Model</label>
              <input
                type="text"
                value={vehicle.model}
                onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })}
                placeholder="Camry"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '8px' }}>
            <label>Starting Odometer (optional)</label>
            <input
              type="number"
              value={vehicle.startingMileage}
              onChange={(e) => setVehicle({ ...vehicle, startingMileage: e.target.value })}
              placeholder="0"
              min="0"
            />
          </div>

          <div className="onboarding-nav-buttons">
            <button className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
            <button
              className="btn btn-primary"
              disabled={!canContinue}
              onClick={() => isNative ? setStep(4) : setStep(4)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: Location Services
  if (step === 4) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card onboarding-welcome">
          <div className="onboarding-progress" style={{ width: '100%', maxWidth: '400px', alignSelf: 'stretch' }}>
            <div className="onboarding-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="onboarding-illustration" style={{ marginTop: '24px' }}>
            <svg viewBox="0 0 120 120" width="120" height="120">
              {/* Phone */}
              <rect x="35" y="10" width="50" height="90" rx="10" fill="#1E293B" stroke="#334155" strokeWidth="1.5" />
              <rect x="40" y="18" width="40" height="68" rx="4" fill="#0F172A" />
              {/* Location pin */}
              <path d="M 60 30 C 50 30 43 37 43 46 C 43 58 55 68 60 74 C 65 68 77 58 77 46 C 77 37 70 30 60 30 Z" fill="none" stroke="#0D9488" strokeWidth="2" />
              <circle cx="60" cy="46" r="5" fill="#14B8A6" />
              {/* Pulse rings */}
              <circle cx="60" cy="46" r="12" fill="none" stroke="#0D9488" strokeWidth="1" opacity="0.3" />
              <circle cx="60" cy="46" r="20" fill="none" stroke="#0D9488" strokeWidth="0.7" opacity="0.15" />
              <circle cx="60" cy="46" r="28" fill="none" stroke="#0D9488" strokeWidth="0.5" opacity="0.08" />
            </svg>
          </div>

          <h2>Enable Location Services</h2>
          <p className="onboarding-desc">
            MileDeduct needs location access to automatically track your trips in the background.
          </p>

          <div className="permission-callout">
            <div className="permission-callout-icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#FBBF24">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            <div className="permission-callout-text">
              {Capacitor.getPlatform() === 'android' ? (
                <><strong>Two prompts will appear.</strong> First, select "While using the app." Then, you'll be taken to Settings — select "Allow all the time" to enable background tracking.</>
              ) : (
                <><strong>Select "Always Allow"</strong> when prompted. This lets the app detect drives automatically, even when it's in the background. Without this, trips won't be recorded.</>
              )}
            </div>
          </div>

          <button className="btn btn-primary" onClick={requestLocation}>
            Enable Location
          </button>
          <button className="btn-skip" onClick={() => setStep(5)}>
            Do this later in Settings
          </button>
        </div>
      </div>
    );
  }

  // Step 5: Motion & Fitness
  if (step === 5) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card onboarding-welcome">
          <div className="onboarding-progress" style={{ width: '100%', maxWidth: '400px', alignSelf: 'stretch' }}>
            <div className="onboarding-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="onboarding-illustration" style={{ marginTop: '24px' }}>
            <svg viewBox="0 0 120 120" width="120" height="120">
              {/* Speedometer arc */}
              <path d="M 25 80 A 40 40 0 0 1 95 80" fill="none" stroke="#334155" strokeWidth="6" strokeLinecap="round" />
              <path d="M 25 80 A 40 40 0 0 1 75 45" fill="none" stroke="url(#motionGrad)" strokeWidth="6" strokeLinecap="round" />
              <defs>
                <linearGradient id="motionGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#0D9488' }} />
                  <stop offset="100%" style={{ stopColor: '#34D399' }} />
                </linearGradient>
              </defs>
              {/* Needle */}
              <line x1="60" y1="78" x2="72" y2="50" stroke="#F8FAFC" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="60" cy="78" r="4" fill="#F8FAFC" />
              {/* Car icon */}
              <rect x="44" y="92" width="32" height="14" rx="4" fill="#1E293B" />
              <circle cx="50" cy="108" r="3" fill="#64748B" />
              <circle cx="70" cy="108" r="3" fill="#64748B" />
            </svg>
          </div>

          <h2>Auto-Drive Detection</h2>
          <p className="onboarding-desc">
            Allow Motion & Fitness access so MileDeduct can automatically detect when you start driving and begin recording trips.
          </p>

          <div className="permission-callout">
            <div className="permission-callout-icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#06B6D4">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div className="permission-callout-text">
              This uses your device's accelerometer and motion sensors to tell the difference between driving, walking, and staying still — so we only track real trips.
            </div>
          </div>

          <button className="btn btn-primary" onClick={requestMotion}>
            Enable Motion & Fitness
          </button>
          <button className="btn-skip" onClick={() => setStep(6)}>
            Do this later
          </button>
        </div>
      </div>
    );
  }

  // Step 6: Notifications
  if (step === 6) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card onboarding-welcome">
          <div className="onboarding-progress" style={{ width: '100%', maxWidth: '400px', alignSelf: 'stretch' }}>
            <div className="onboarding-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="onboarding-illustration" style={{ marginTop: '24px' }}>
            <svg viewBox="0 0 120 120" width="120" height="120">
              <circle cx="60" cy="50" r="35" fill="#1E293B" />
              <path d="M 60 25 C 48 25 40 33 40 42 L 40 55 L 35 62 L 85 62 L 80 55 L 80 42 C 80 33 72 25 60 25Z" fill="#334155" />
              <rect x="52" y="62" width="16" height="6" rx="3" fill="#64748B" />
              <circle cx="60" cy="42" r="4" fill="#34D399" />
              <circle cx="60" cy="42" r="8" fill="none" stroke="#34D399" strokeWidth="1" opacity="0.3" />
              <circle cx="60" cy="42" r="14" fill="none" stroke="#34D399" strokeWidth="0.5" opacity="0.15" />
            </svg>
          </div>
          <h2>Never miss a trip</h2>
          <p className="onboarding-desc">
            Enable push notifications to get alerts when a trip is detected and see your savings in real time.
          </p>

          <div className="onboarding-notif-preview">
            <div className="notif-preview-header">MILEDEDUCT</div>
            <div className="notif-preview-title">New trip: $12.40 potential!</div>
            <div className="notif-preview-body">Work or personal? Classify to claim.</div>
          </div>

          <button className="btn btn-primary" onClick={requestNotifications} disabled={loading}>
            {loading ? 'Saving...' : 'Enable Notifications'}
          </button>
          <button className="btn-skip" onClick={requestNotifications} disabled={loading}>
            Do this later
          </button>
        </div>
      </div>
    );
  }

  // Subscription step (step 4 on web, step 7 on native)
  if ((isNative && step === 7) || (!isNative && step === 4)) {
    const FEATURES = [
      'Automatic drive detection',
      'GPS mileage tracking',
      'IRS deduction calculator',
      'Receipt scanning & OCR',
      'Expense tracking',
      'Trip history & reports',
      'CSV export',
    ];

    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card onboarding-welcome">
          <div className="onboarding-progress" style={{ width: '100%', maxWidth: '400px', alignSelf: 'stretch' }}>
            <div className="onboarding-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="onboarding-illustration" style={{ marginTop: '24px' }}>
            <svg viewBox="0 0 120 120" width="120" height="120">
              <circle cx="60" cy="60" r="45" fill="#134E4A" />
              <text x="60" y="55" textAnchor="middle" fontSize="32" fontWeight="800" fill="#34D399">$</text>
              <text x="60" y="80" textAnchor="middle" fontSize="11" fontWeight="600" fill="#5EEAD4">SAVE MORE</text>
            </svg>
          </div>

          <h2>Start Your Free Trial</h2>
          <p className="onboarding-desc">
            Try MileDeduct free for 30 days. Cancel anytime.
          </p>

          <div className="plan-price" style={{ textAlign: 'center', margin: '16px 0' }}>
            <span style={{ fontSize: '32px', fontWeight: 800, color: '#0F172A' }}>$39.99</span>
            <span style={{ fontSize: '16px', color: '#64748B' }}>/year</span>
            <div style={{ fontSize: '13px', color: '#14B8A6', marginTop: '4px' }}>after 30-day free trial</div>
          </div>

          <ul className="plan-features" style={{ textAlign: 'left', margin: '16px 0', listStyle: 'none', padding: 0 }}>
            {FEATURES.map((f, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', fontSize: '14px', color: '#334155' }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="#0D9488">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                {f}
              </li>
            ))}
          </ul>

          <button className="btn btn-primary" onClick={handleSubscribe} disabled={checkoutLoading}>
            {checkoutLoading ? 'Loading...' : 'Start 30-Day Free Trial'}
          </button>
          <button className="btn-skip" onClick={skipSubscription}>
            Maybe later
          </button>
          <p style={{ fontSize: '11px', color: '#94A3B8', textAlign: 'center', marginTop: '8px' }}>
            Card required. Cancel anytime before trial ends.
          </p>
        </div>
      </div>
    );
  }

  // Step 8: All Set (native) / Step 5: All Set (web)
  if (step === 8 || (!isNative && step === 5)) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card onboarding-welcome">
          <div className="onboarding-illustration">
            <svg viewBox="0 0 160 120" width="160" height="120">
              <path d="M 20 90 Q 50 30 80 60 Q 110 90 140 50" fill="none" stroke="#0D9488" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="140" cy="50" r="18" fill="#134E4A" />
              <path d="M 132 50 L 138 56 L 150 44" fill="none" stroke="#34D399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2>You're all set!</h2>
          <p className="onboarding-desc">
            We'll automatically track your trips as long as the app is running in the background. Every mile is money back.
          </p>
          <button className="btn btn-primary" onClick={onComplete}>
            Start Tracking
          </button>
        </div>
      </div>
    );
  }

  return null;
}
